import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

interface Snowflake {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
}

const Snowglobe = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [shakeIntensity, setShakeIntensity] = useState(0);
  const snowflakesRef = useRef<Snowflake[]>([]);
  const mousePos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", resize);
    resize();

    const createSnowflake = (): Snowflake => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 3 + 1,
      speedX: (Math.random() - 0.5) * 1,
      speedY: Math.random() * 1 + 0.5,
      opacity: Math.random() * 0.5 + 0.3,
    });

    snowflakesRef.current = Array.from({ length: 200 }, createSnowflake);

    const handleMouseMove = (e: MouseEvent) => {
      const dx = e.clientX - mousePos.current.x;
      const dy = e.clientY - mousePos.current.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist > 50) {
        setShakeIntensity(Math.min(dist / 10, 15));
      }
      mousePos.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener("mousemove", handleMouseMove);

    let animationFrameId: number;

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const intensity = shakeIntensity;
      setShakeIntensity((prev) => prev * 0.95);

      ctx.fillStyle = "white";

      snowflakesRef.current.forEach((s) => {
        s.speedX += (Math.random() - 0.5) * intensity * 0.1;
        s.speedY += (Math.random() - 0.5) * intensity * 0.1;
        s.speedY += 0.02;
        s.x += s.speedX;
        s.y += s.speedY;

        if (s.x < 0) s.x = canvas.width;
        if (s.x > canvas.width) s.x = 0;
        if (s.y < 0) s.y = canvas.height;
        if (s.y > canvas.height) {
          s.y = -10;
          s.speedY = Math.random() * 1 + 0.5;
        }

        s.speedX *= 0.99;
        s.speedY *= 0.99;

        ctx.globalAlpha = s.opacity;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.globalAlpha = 1.0;
      ctx.strokeStyle = "rgba(255, 255, 255, 0.2)";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.arc(
        canvas.width / 2,
        canvas.height / 2,
        Math.min(canvas.width, canvas.height) * 0.4,
        0,
        Math.PI * 2,
      );
      ctx.stroke();

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [shakeIntensity]);

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#0a192f",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: "white",
        fontFamily: "sans-serif",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "20px",
          textAlign: "center",
          pointerEvents: "none",
          opacity: 0.7,
        }}
      >
        <h1 style={{ margin: 0 }}>Snowglobe</h1>
        <p style={{ margin: "5px 0 0" }}>Shake your mouse to stir the snow</p>
      </div>
      <canvas
        ref={canvasRef}
        style={{
          display: "block",
          filter: "blur(1px)",
        }}
      />
    </div>
  );
};

const container = document.getElementById("root");
if (!container) {
  throw new Error("Missing #root element");
}
const root = createRoot(container);
root.render(<Snowglobe />);
